/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { animated, useTransition } from 'react-spring'

import userAvatarUrl from '../../assets/img/user.png'
import { CookieModal } from '../../modals/CookieModal.tsx'
import { getCurrentUser } from '../../store/actions/authActions.ts'
import { RootState, useAppDispatch } from '../../store/index.ts'
import { useUserMenu } from './useUserMenu.tsx'

export const UserMenu = () => {
  const dispatch = useAppDispatch()
  const {
    user: { data: user },
    loading,
  } = useSelector((state: RootState) => state.userReducer)

  const [showDropdown, setShowDropdown] = useState(false)
  const [showCookieModal, setShowCookieModal] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null) // Create a ref for the dropdown menu
  const buttonRef = useRef<HTMLButtonElement>(null) // Create a ref for open user menu button

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef?.current?.contains(event.target as Node)
      ) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef, buttonRef])

  // Fetch user data
  useEffect(() => {
    if (!user?.id && !loading) {
      dispatch(getCurrentUser())
    }
  }, [])

  const closeMenu = () => {
    setShowDropdown(false)
  }

  const openCookieModal = () => {
    closeMenu()
    setShowCookieModal(true)
  }

  const userMenu = useUserMenu({ closeMenu, openCookieModal })

  const transitions = useTransition(showDropdown, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 250 },
  })

  // Get user avatar
  const avatarUrl = user?.avatar
    ? `${user.avatar}?${new Date().getTime()}`
    : userAvatarUrl

  return (
    <>
      <div>
        <button
          ref={buttonRef}
          id="avatarButton"
          className="h-10 w-10 cursor-pointer overflow-hidden rounded-full focus:outline-none"
          onClick={() => setShowDropdown((prevState) => !prevState)}
        >
          <div className={`mb-6 flex w-full items-center justify-center`}>
            <label
              className="flex h-[40px] w-[40px] flex-col cursor-pointer 
          items-center justify-center overflow-hidden rounded-full bg-[#c2c2c2]"
            >
              <div className="pointer-events-none flex flex-col items-center justify-center">
                <span className="text-[24px] text-secondary">
                  {user?.username.slice(0, 1).toUpperCase()}
                </span>
              </div>
            </label>
          </div>
        </button>

        {/* Drop down menu */}
        {transitions((styles: any, item: any) =>
          item ? (
            <animated.div
              style={styles}
              ref={dropdownRef}
              className=" absolute right-4 top-16 z-10 w-[300px] rounded-lg border border-gray-300 bg-white px-2.5 py-3 shadow-md"
            >
              <div className="mb-2.5 flex items-center gap-1.5">
                <label
                  className="mr-2 flex h-[40px] w-[40px]
          flex-col items-center justify-center overflow-hidden rounded-full bg-[#c2c2c2]"
                >
                  <div className="pointer-events-none flex flex-col items-center justify-center">
                    <span className="text-[24px] text-secondary">
                      {user?.username.slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                </label>

                <div>
                  <p className="text-md font-bold capitalize leading-none">
                    {user?.username}
                  </p>
                  <p className="text-sm text-textSecondary underline">
                    {user?.email}
                  </p>
                </div>
              </div>

              <ul>
                {userMenu.map(({ href, label, handleClick, Icon }, index) => (
                  <li key={index}>
                    {href ? (
                      <Link
                        to={href}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-gray-800  hover:bg-gray-200"
                        onClick={handleClick}
                      >
                        <Icon width={36} />
                        <span className="text-md">{label}</span>
                      </Link>
                    ) : (
                      <button
                        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-gray-800 hover:bg-gray-200"
                        onClick={handleClick}
                      >
                        <Icon width={36} />
                        <span className="text-md">{label}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </animated.div>
          ) : null,
        )}
      </div>

      <CookieModal
        showModal={showCookieModal}
        onClose={() => setShowCookieModal(false)}
        onConfirm={() => {}}
      />
    </>
  )
}
